import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'

import type { PromotionScheduleStatusType } from '@/api/admin-promotions-types'
import { preloadListPromotions } from '@/api/react-query'
import { restoreListFilters } from '@/utils/list-filter-persistence'
import { paginatedSearchWithSort } from '@/utils/route-utilities'

export const PROMOTIONS_FILTER_STORAGE_KEY = 'promotions'
export const PROMOTIONS_FILTER_KEYS = [
	'q',
	'salesChannels',
	'promotionGroups',
	'status',
	'sortBy',
	'sortOrder'
] as const

export const Route = createFileRoute('/_authenticated/_general/promotions')({
	validateSearch: paginatedSearchWithSort(['name', 'enabled', 'rank', 'startDate', 'endDate']).extend({
		salesChannels: z.array(z.string()).optional(),
		promotionGroups: z.array(z.string()).optional(),
		status: z.array(z.string()).optional()
	}),
	beforeLoad: ({ search, location, cause }) =>
		restoreListFilters({
			storageKey: PROMOTIONS_FILTER_STORAGE_KEY,
			to: '/promotions',
			pathname: location.pathname,
			cause,
			keys: PROMOTIONS_FILTER_KEYS,
			search,
			// Promotions default to the ones a merchant usually cares about; Expired/Inactive are
			// hidden until the user opts into them (or clears the filter to see everything).
			defaults: { status: ['ACTIVE', 'SCHEDULED'] }
		}),
	loaderDeps: ({ search: { limit, offset, q, sortBy, sortOrder, salesChannels, promotionGroups, status } }) => ({
		limit,
		offset,
		q,
		sortBy,
		sortOrder,
		salesChannels,
		promotionGroups,
		status
	}),
	loader: async ({
		context,
		deps: { limit, offset, q, sortBy, sortOrder, salesChannels, promotionGroups, status }
	}) =>
		await preloadListPromotions(context, {
			query: {
				limit: limit.toString(),
				offset: offset?.toString(),
				q,
				sortBy,
				sortOrder,
				salesChannels: salesChannels?.length ? salesChannels : undefined,
				promotionGroups: promotionGroups?.length ? promotionGroups : undefined,
				status: (status?.length ? status : undefined) as PromotionScheduleStatusType[] | undefined
			}
		}),
	staticData: {
		crumb: ({ context: { L } }) => L.Promotions.Title()
	}
})
